'use client';

// Gatilho clicável nos cards de planos ("<N> eventos/mês") que abre o popup
// explicando o que conta como evento + tabela comparativa de limites por plano.
// Usa o Modal padrão do Trakd. Estilos das classes ev-link/evpop-* vivem em
// globals.css (compartilhados entre home e /precos, sem duplicar).

import { useState, type ReactElement } from 'react';
import { TbBolt } from 'react-icons/tb';

import { Modal } from '@/components/ui/modal';

const EVENTS: { label: string; tech: string }[] = [
  { label: 'Visita de página', tech: 'PageView' },
  { label: 'Viu um produto', tech: 'ViewContent' },
  { label: 'Add ao carrinho', tech: 'AddToCart' },
  { label: 'Início de checkout', tech: 'InitiateCheckout' },
  { label: 'Compra', tech: 'Purchase' },
  { label: 'Assinatura', tech: 'Subscribe' },
  { label: 'Cancelamento', tech: 'CancelSubscription' },
  { label: 'Reembolso', tech: 'Refund' },
];

const PLANS: { name: string; events: string; width: string; hi?: boolean }[] = [
  { name: 'Solo', events: '150 mil', width: '8%' },
  { name: 'Pro', events: '500 mil', width: '18%', hi: true },
  { name: 'Escala', events: '1,5 milhão', width: '45%' },
  { name: 'Agência', events: '5 milhões', width: '100%' },
];

export function EventsLimitPopup({ label }: { label: string }): ReactElement {
  const [open, setOpen] = useState(false);

  return (
    <>
      <span
        className="ev-link"
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {label}
      </span>

      <Modal
        open={open}
        onOpenChange={setOpen}
        tone="brand"
        icon={<TbBolt size={20} />}
        title="O que são eventos/mês?"
        description="Cada ação rastreada no seu site conta como 1 evento."
        footer={
          <>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="border-border-subtle text-ink-muted hover:text-ink cursor-pointer rounded-[10px] border px-[18px] py-2.5 text-[13px] font-semibold transition-colors"
            >
              Entendi
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="cursor-pointer rounded-[10px] bg-[linear-gradient(120deg,#EAE7FB,#9061FF_75%)] px-[18px] py-2.5 text-[13px] font-semibold text-[#1B1530] shadow-[0_8px_22px_rgba(144,97,255,0.4)] transition-[filter] hover:brightness-[1.08]"
            >
              Ver planos
            </button>
          </>
        }
      >
        <p className="evpop-lead">
          A Trakd rastreia as ações dos visitantes e manda pra Meta via <b>CAPI (server-side)</b>.
          Cada ação enviada conta como <b>1 evento</b>. O limite do seu plano é a{' '}
          <b>soma de todos os eventos no mês</b>: não é só venda, toda interação rastreada entra na
          conta.
        </p>

        <div>
          <p className="evpop-label">Cada um destes conta como 1 evento</p>
          <div className="evpop-grid">
            {EVENTS.map((e) => (
              <div className="evpop-chip" key={e.tech}>
                <span className="dot" aria-hidden />
                {e.label}
                <span className="tech">{e.tech}</span>
              </div>
            ))}
          </div>
          <p className="evpop-more">E outros: Lead, StartTrial, AddPaymentInfo, Chargeback…</p>
        </div>

        <div>
          <p className="evpop-label">Quanto cada plano inclui por mês</p>
          <div className="evpop-cmp">
            {PLANS.map((p) => (
              <div className={p.hi ? 'evpop-row hi' : 'evpop-row'} key={p.name}>
                <span className="pname">
                  {p.name}
                  {p.hi && <span className="evpop-badge">POPULAR</span>}
                </span>
                <span className="track">
                  <span className="fill" style={{ width: p.width }} />
                </span>
                <span className="pval">{p.events}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="evpop-label" style={{ marginBottom: 10 }}>
            E se passar do limite?
          </p>
          <p className="evpop-xblk">
            Cada evento extra custa <b>R$ 0,003</b> (R$ 3 a cada 1.000 eventos), pague só pelo que
            usar. Você <b>define um teto</b> de gasto ou bloqueia no limite do plano, sem surpresa
            na fatura.
          </p>
        </div>
      </Modal>
    </>
  );
}
